import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { FORUM_ENABLED } from "../lib/features";
import {
  forumGetTopics,
  forumSignup,
  forumLogin,
  forumMe,
  forumListPosts,
  forumVotePost,
  forumListComments,
  forumCreateComment,
  forumCreateReply,
  forumVoteComment,
  forumDeletePost,
  forumDeleteComment,
  forumLogout,
} from "../lib/api";

const FORUM_TOKEN_KEY = "rl_forum_token_v1";

export default function Forum() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [token, setToken] = React.useState(() => localStorage.getItem(FORUM_TOKEN_KEY) || "");
  const [user, setUser] = React.useState(null);
  const [topics, setTopics] = React.useState({});
  const [limits, setLimits] = React.useState({ postsPerDay: 3, commentsPerDay: 10, postsRemainingToday: 3, commentsRemainingToday: 10 });
  const [topic, setTopic] = React.useState(() => searchParams.get("topic") || "recovery_help");
  const [posts, setPosts] = React.useState([]);
  const [selectedPostId, setSelectedPostId] = React.useState("");
  const [comments, setComments] = React.useState([]);
  const [authMode, setAuthMode] = React.useState("signup");
  const [authForm, setAuthForm] = React.useState({ username: "", email: "", identifier: "", password: "" });
  const [commentBody, setCommentBody] = React.useState("");
  const [replyOpenId, setReplyOpenId] = React.useState("");
  const [replyDrafts, setReplyDrafts] = React.useState({});
  const [showDeepRepliesFor, setShowDeepRepliesFor] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const commentListRef = React.useRef(null);
  const [commentScrollHint, setCommentScrollHint] = React.useState(false);

  const selectedPost = React.useMemo(() => posts.find((p) => p.id === selectedPostId) || null, [posts, selectedPostId]);
  const commentsByParent = React.useMemo(() => {
    const grouped = {};
    for (const c of comments) {
      const key = c.parentCommentId || "root";
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(c);
    }
    Object.keys(grouped).forEach((k) => {
      grouped[k].sort((a, b) => {
        const scoreDelta = Number(b.score || 0) - Number(a.score || 0);
        if (scoreDelta !== 0) return scoreDelta;
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      });
    });
    return grouped;
  }, [comments]);

  const loadTopics = React.useCallback(async () => {
    const data = await forumGetTopics();
    setTopics(data?.groups || {});
  }, []);

  const loadMe = React.useCallback(async (tok) => {
    if (!tok) return;
    const me = await forumMe(tok);
    setUser(me?.user || null);
    setLimits((prev) => ({ ...prev, ...(me?.limits || {}) }));
  }, []);

  const loadPosts = React.useCallback(async (tok, nextTopic = topic) => {
    if (!tok) return;
    setLoading(true);
    try {
      const data = await forumListPosts({ token: tok, topic: nextTopic, sort: "top", page: 1, pageSize: 30 });
      const items = Array.isArray(data?.items) ? data.items : [];
      setPosts(items);
      setSelectedPostId((prev) => (prev && items.find((p) => p.id === prev) ? prev : (items[0]?.id || "")));
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load forum posts.");
    } finally {
      setLoading(false);
    }
  }, [topic]);

  const loadComments = React.useCallback(async (tok, postId) => {
    if (!tok || !postId) {
      setComments([]);
      return;
    }
    try {
      const data = await forumListComments({ token: tok, postId, sort: "top", page: 1, pageSize: 150 });
      setComments(Array.isArray(data?.items) ? data.items : []);
    } catch {
      setComments([]);
    }
  }, []);

  React.useEffect(() => {
    if (!FORUM_ENABLED) return;
    loadTopics();
  }, [loadTopics]);

  React.useEffect(() => {
    const run = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        await loadMe(token);
        await loadPosts(token);
      } catch {
        localStorage.removeItem(FORUM_TOKEN_KEY);
        setToken("");
        setUser(null);
      }
    };
    run();
  }, [token, loadMe, loadPosts]);

  React.useEffect(() => {
    if (!token || !selectedPostId) return;
    loadComments(token, selectedPostId);
  }, [token, selectedPostId, loadComments]);

  React.useEffect(() => {
    const next = searchParams.get("topic") || "recovery_help";
    if (next !== topic) setTopic(next);
  }, [searchParams, topic]);

  React.useEffect(() => {
    const el = commentListRef.current;
    if (!el) return undefined;

    const updateHint = () => {
      const canScroll = el.scrollHeight > el.clientHeight + 6;
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
      setCommentScrollHint(canScroll && !nearBottom);
    };

    updateHint();
    el.addEventListener("scroll", updateHint);
    window.setTimeout(updateHint, 50);
    return () => el.removeEventListener("scroll", updateHint);
  }, [comments, selectedPostId]);

  if (!FORUM_ENABLED) {
    return <div className="mx-auto max-w-3xl px-4 py-10 text-sm text-slate-500" data-testid="forum-disabled-view">Forum is currently disabled.</div>;
  }

  const onAuthSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = authMode === "signup"
        ? { username: authForm.username, email: authForm.email, password: authForm.password }
        : { identifier: authForm.identifier, password: authForm.password };
      const data = authMode === "signup" ? await forumSignup(payload) : await forumLogin(payload);
      const tok = data?.token || "";
      if (!tok) throw new Error("No token");
      localStorage.setItem(FORUM_TOKEN_KEY, tok);
      setToken(tok);
      setError("");
    } catch (e2) {
      setError(e2?.response?.data?.detail || "Auth failed.");
    }
  };

  const onVotePost = async (postId, nextValue) => {
    if (!token) return;
    await forumVotePost({ token, postId, value: nextValue });
    await loadPosts(token, topic);
  };

  const onCreateComment = async (e) => {
    e.preventDefault();
    if (!token || !selectedPostId) return;
    try {
      await forumCreateComment({ token, postId: selectedPostId, body: commentBody });
      setCommentBody("");
      await loadMe(token);
      await loadPosts(token, topic);
      await loadComments(token, selectedPostId);
    } catch (e4) {
      setError(e4?.response?.data?.detail || "Could not add comment.");
    }
  };

  const onCreateReply = async (parentCommentId) => {
    if (!token || !selectedPostId) return;
    const body = String(replyDrafts[parentCommentId] || "").trim();
    if (!body) return;
    try {
      await forumCreateReply({ token, postId: selectedPostId, body, parentCommentId });
      setReplyDrafts((s) => ({ ...s, [parentCommentId]: "" }));
      setReplyOpenId("");
      await loadMe(token);
      await loadPosts(token, topic);
      await loadComments(token, selectedPostId);
    } catch (e5) {
      setError(e5?.response?.data?.detail || "Could not add reply.");
    }
  };

  const onVoteComment = async (commentId, nextValue) => {
    if (!token || !selectedPostId) return;
    await forumVoteComment({ token, commentId, value: nextValue });
    await loadComments(token, selectedPostId);
  };

  const onOwnerDeletePost = async (postId) => {
    if (!token || !user?.isOwner) return;
    if (!window.confirm("Delete this post and all replies permanently?")) return;
    try {
      await forumDeletePost({ token, postId });
      await loadPosts(token, topic);
      if (selectedPostId === postId) {
        const next = posts.find((p) => p.id !== postId);
        setSelectedPostId(next?.id || "");
      }
      if (selectedPostId) await loadComments(token, selectedPostId);
    } catch (e6) {
      setError(e6?.response?.data?.detail || "Could not delete post.");
    }
  };

  const onOwnerDeleteComment = async (commentId) => {
    if (!token || !user?.isOwner || !selectedPostId) return;
    if (!window.confirm("Delete this comment thread permanently?")) return;
    try {
      await forumDeleteComment({ token, commentId });
      await loadPosts(token, topic);
      await loadComments(token, selectedPostId);
    } catch (e7) {
      setError(e7?.response?.data?.detail || "Could not delete comment.");
    }
  };

  const renderCommentTree = (parentId = "root", level = 0) => {
    const items = commentsByParent[parentId] || [];
    if (!items.length) return null;
    return (
      <div className="space-y-2" data-testid={`forum-comment-children-${parentId || "root"}`}>
        {items.map((c) => {
          const childList = commentsByParent[c.id] || [];
          const isDepthCapped = level >= 4;
          const expanded = Boolean(showDeepRepliesFor[c.id]);
          const showChildren = !isDepthCapped || expanded;
          const indentPx = Math.min(level, 4) * 14;

          return (
            <div key={c.id} style={{ marginLeft: `${indentPx}px` }} className="rounded-lg border border-slate-300 dark:border-slate-700 p-2 bg-white dark:bg-slate-900" data-testid={`forum-comment-item-${c.id}`}>
              <div className="text-xs text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                <span>u/{c.authorUsername}</span>
                {c.authorIsOwner && <span className="px-1.5 py-0.5 rounded-full bg-blue-600 text-white" data-testid={`forum-owner-pill-comment-${c.id}`}>Owner</span>}
                <span>· {new Date(c.createdAt).toLocaleString()}</span>
              </div>
              <div className="text-sm mt-1 whitespace-pre-wrap text-slate-900 dark:text-slate-100">{c.body}</div>
              <div className="mt-2 flex items-end justify-between gap-2 text-xs">
                <div className="flex items-center gap-2 flex-wrap">
                  <Button type="button" size="sm" variant="outline" onClick={() => onVoteComment(c.id, c.viewerVote === 1 ? 0 : 1)} data-testid={`forum-comment-upvote-${c.id}`}>▲</Button>
                  <span className="text-slate-900 dark:text-slate-100" data-testid={`forum-comment-score-${c.id}`}>{c.score}</span>
                  <Button type="button" size="sm" variant="outline" onClick={() => onVoteComment(c.id, c.viewerVote === -1 ? 0 : -1)} data-testid={`forum-comment-downvote-${c.id}`}>▼</Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => setReplyOpenId((prev) => (prev === c.id ? "" : c.id))} data-testid={`forum-comment-reply-toggle-${c.id}`}>Reply</Button>
                </div>
                {user?.isOwner && (
                  <Button type="button" size="sm" className="bg-red-700 hover:bg-red-800 text-white" onClick={() => onOwnerDeleteComment(c.id)} data-testid={`forum-comment-remove-${c.id}`}>Remove</Button>
                )}
              </div>

              {replyOpenId === c.id && (
                <div className="mt-2 space-y-2" data-testid={`forum-comment-reply-form-${c.id}`}>
                  <textarea
                    className="w-full min-h-[70px] rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 px-3 py-2 text-sm placeholder:text-slate-500 dark:placeholder:text-slate-400"
                    value={replyDrafts[c.id] || ""}
                    onChange={(e) => setReplyDrafts((s) => ({ ...s, [c.id]: e.target.value }))}
                    placeholder={`Reply to u/${c.authorUsername}...`}
                    data-testid={`forum-comment-reply-input-${c.id}`}
                  />
                  <div className="flex justify-end">
                    <Button type="button" onClick={() => onCreateReply(c.id)} data-testid={`forum-comment-reply-submit-${c.id}`}>Reply</Button>
                  </div>
                </div>
              )}

              {!!childList.length && !showChildren && (
                <div className="mt-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setShowDeepRepliesFor((s) => ({ ...s, [c.id]: true }))}
                    data-testid={`forum-comment-show-more-${c.id}`}
                  >
                    Show more replies ({childList.length})
                  </Button>
                </div>
              )}

              {!!childList.length && showChildren && renderCommentTree(c.id, level + 1)}
            </div>
          );
        })}
      </div>
    );
  };

  const onLogout = async () => {
    try { await forumLogout(token); } catch (_) {}
    localStorage.removeItem(FORUM_TOKEN_KEY);
    setToken("");
    setUser(null);
    setPosts([]);
    setComments([]);
  };

  if (!token || !user) {
    return (
      <div className="mx-auto max-w-xl px-4 py-8 sm:py-12" data-testid="forum-auth-screen">
        <Card className="border border-emerald-300/60 dark:border-emerald-700/50 bg-white dark:bg-slate-900 shadow-[0_10px_30px_rgba(16,185,129,0.12)]">
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <CardTitle data-testid="forum-auth-title">Forum Sign Up / Login</CardTitle>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setAuthMode("login");
                  setAuthForm((s) => ({ ...s, identifier: "" }));
                }}
                data-testid="forum-admin-login-shortcut"
              >
                Admin Login
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2" data-testid="forum-auth-mode-row">
              <Button variant={authMode === "signup" ? "default" : "outline"} onClick={() => setAuthMode("signup")} data-testid="forum-auth-signup-tab">Sign up</Button>
              <Button variant={authMode === "login" ? "default" : "outline"} onClick={() => setAuthMode("login")} data-testid="forum-auth-login-tab">Login</Button>
            </div>
            <form onSubmit={onAuthSubmit} className="space-y-3" data-testid="forum-auth-form">
              {authMode === "signup" ? (
                <>
                  <input className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 px-3 py-2 text-sm placeholder:text-slate-500 dark:placeholder:text-slate-400" placeholder="Username" value={authForm.username} onChange={(e) => setAuthForm((s) => ({ ...s, username: e.target.value }))} data-testid="forum-signup-username-input" />
                  <input className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 px-3 py-2 text-sm placeholder:text-slate-500 dark:placeholder:text-slate-400" placeholder="Email" value={authForm.email} onChange={(e) => setAuthForm((s) => ({ ...s, email: e.target.value }))} data-testid="forum-signup-email-input" />
                </>
              ) : (
                <input className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 px-3 py-2 text-sm placeholder:text-slate-500 dark:placeholder:text-slate-400" placeholder="Username or email" value={authForm.identifier} onChange={(e) => setAuthForm((s) => ({ ...s, identifier: e.target.value }))} data-testid="forum-login-identifier-input" />
              )}
              <input type="password" className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 px-3 py-2 text-sm placeholder:text-slate-500 dark:placeholder:text-slate-400" placeholder="Password" value={authForm.password} onChange={(e) => setAuthForm((s) => ({ ...s, password: e.target.value }))} data-testid="forum-auth-password-input" />
              <Button type="submit" className="w-full" data-testid="forum-auth-submit-button">{authMode === "signup" ? "Create account" : "Login"}</Button>
            </form>
            {!!error && <div className="text-sm text-red-600" data-testid="forum-auth-error">{error}</div>}
          </CardContent>
        </Card>
      </div>
    );
  }

  const topicEntries = Object.entries(topics || {});

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:py-10 space-y-4" data-testid="forum-page">
      <div className="flex flex-wrap items-center justify-between gap-3" data-testid="forum-header">
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight" data-testid="forum-page-title">Community Forum</h1>
        <div className="flex items-center gap-2 text-sm" data-testid="forum-user-chip">
          <span className="rounded-full bg-emerald-100 text-emerald-900 px-3 py-1">@{user.username}</span>
          <Button variant="outline" onClick={onLogout} data-testid="forum-logout-button">Logout</Button>
        </div>
      </div>

      {!!error && <div className="text-sm text-red-600" data-testid="forum-error-banner">{error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-[250px,1fr,360px] gap-4" data-testid="forum-layout">
        <Card className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700">
          <CardHeader>
            <div className="flex items-center justify-between gap-2" data-testid="forum-areas-header-row">
              <CardTitle className="text-base">Areas</CardTitle>
              <button
                type="button"
                onClick={() => nav(`/forum/create?topic=${encodeURIComponent(topic)}`)}
                className="inline-flex items-center gap-2 rounded-lg border border-emerald-600/70 bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-[0_8px_18px_rgba(16,185,129,0.28)]"
                data-testid="forum-create-post-link-button"
              >
                <span>Create Post</span>
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-emerald-700 text-white text-base leading-none font-bold">+</span>
              </button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4" data-testid="forum-topic-groups">
            {topicEntries.map(([groupName, arr]) => (
              <div key={groupName} data-testid={`forum-topic-group-${groupName.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="text-xs uppercase tracking-wide text-slate-700 dark:text-slate-300 mb-2">{groupName}</div>
                <div className="space-y-1">
                  {(arr || []).map((t) => (
                    <button key={t.slug} className={`w-full text-left px-3 py-2 rounded-lg text-sm border ${topic === t.slug ? "bg-emerald-600 text-white border-emerald-600" : "text-slate-900 dark:text-slate-100 border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"}`} onClick={() => { setTopic(t.slug); setSearchParams((prev) => { const p = new URLSearchParams(prev); p.set("topic", t.slug); return p; }); loadPosts(token, t.slug); }} data-testid={`forum-topic-button-${t.slug}`}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="space-y-4" data-testid="forum-center-column">
          <Card className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between mb-0" data-testid="forum-controls-row">
                <div className="text-xs text-slate-500 dark:text-slate-400" data-testid="forum-score-order-note">Ordered by likes</div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700">
            <CardContent className="pt-4 space-y-2" data-testid="forum-post-list">
              {loading && <div className="text-sm text-slate-700 dark:text-slate-200">Loading posts…</div>}
              {!loading && posts.length === 0 && <div className="text-sm text-slate-700 dark:text-slate-200">No posts yet in this area.</div>}
              {posts.map((p) => (
                <div key={p.id} className={`w-full text-left rounded-xl border p-3 ${selectedPostId === p.id ? "border-emerald-500 bg-emerald-50/50 dark:bg-emerald-900/20" : "border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"}`} onClick={() => setSelectedPostId(p.id)} data-testid={`forum-post-item-${p.id}`} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter") setSelectedPostId(p.id); }}>
                  <div className="font-semibold text-sm text-slate-900 dark:text-slate-100">{p.title}</div>
                  <div className="text-xs text-slate-700 dark:text-slate-300 mt-1 flex items-center gap-1.5">
                    <span>u/{p.authorUsername}</span>
                    {p.authorIsOwner && <span className="px-1.5 py-0.5 rounded-full bg-blue-600 text-white" data-testid={`forum-owner-pill-post-${p.id}`}>Owner</span>}
                    <span>· {new Date(p.createdAt).toLocaleString()}</span>
                  </div>
                  <div className="text-sm mt-2 line-clamp-3 text-slate-900 dark:text-slate-100">{p.body}</div>
                  <div className="mt-2 flex items-end justify-between gap-2 text-xs">
                    <div className="flex items-center gap-2">
                      <Button type="button" size="sm" variant="outline" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onVotePost(p.id, p.viewerVote === 1 ? 0 : 1); }} data-testid={`forum-post-upvote-${p.id}`}>▲</Button>
                      <span className="text-slate-900 dark:text-slate-100" data-testid={`forum-post-score-${p.id}`}>{p.score}</span>
                      <Button type="button" size="sm" variant="outline" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onVotePost(p.id, p.viewerVote === -1 ? 0 : -1); }} data-testid={`forum-post-downvote-${p.id}`}>▼</Button>
                      <span className="ml-2 text-slate-700 dark:text-slate-300" data-testid={`forum-post-comment-count-${p.id}`}>{p.commentCount} comments</span>
                    </div>
                    {user?.isOwner && (
                      <Button
                        type="button"
                        size="sm"
                        className="bg-red-700 hover:bg-red-800 text-white"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onOwnerDeletePost(p.id);
                        }}
                        data-testid={`forum-post-remove-${p.id}`}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        <Card className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="text-base" data-testid="forum-comments-title">Comments</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3" data-testid="forum-comments-panel">
            <div className="text-xs text-slate-700 dark:text-slate-300">Comments left today: <span className="font-semibold" data-testid="forum-comments-remaining-value">{limits.commentsRemainingToday}</span></div>
            {!selectedPost && <div className="text-sm text-slate-700 dark:text-slate-200">Select a post to read comments.</div>}
            {!!selectedPost && (
              <>
                <div className="rounded-lg border border-slate-300 dark:border-slate-700 p-2 text-sm bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100" data-testid="forum-selected-post-title">{selectedPost.title}</div>
                <form onSubmit={onCreateComment} className="space-y-2" data-testid="forum-create-comment-form">
                  <textarea className="w-full min-h-[70px] rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 px-3 py-2 text-sm placeholder:text-slate-500 dark:placeholder:text-slate-400" value={commentBody} onChange={(e) => setCommentBody(e.target.value)} placeholder="Write a comment..." data-testid="forum-comment-body-input" />
                  <div className="flex justify-end"><Button type="submit" data-testid="forum-comment-submit-button">Comment</Button></div>
                </form>

                <div className="relative" data-testid="forum-comment-list-wrapper">
                  <div ref={commentListRef} className="space-y-2 max-h-[460px] overflow-auto pr-1" data-testid="forum-comment-list">
                    {renderCommentTree("root", 0)}
                  </div>
                  {commentScrollHint && (
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-white/95 to-transparent dark:from-slate-900/95 py-2 text-center text-xs font-semibold text-emerald-700 dark:text-emerald-300" data-testid="forum-comment-scroll-hint">
                      Scroll for more replies ↓
                    </div>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
